import { Schema } from "effect";

import { DashboardEditOperation } from "./dashboardEdits";
import {
  DashboardFreshness,
  DashboardWidgetResolutionFields,
  dashboardWidgetResolutionFilter,
} from "./dashboardResolve";
import { Identifier, PositiveCount, ShortText } from "./dashboardSchemaPrimitives";
import { MetricValue, Provenance } from "./metrics";

/**
 * The dashboard assistant, over the existing `/ws/t3` socket (no second
 * socket). The page sends `assistant.send` and `assistant.cancel` requests and
 * listens on the `dashboard.assistant.event` push channel.
 *
 * Threads: one persisted thread per dashboard per user. A new, unsaved
 * dashboard starts with `dashboardRef: null` and a client-made `threadId`;
 * the first `edits.applied` names the dashboard it created (`dashboardRef`),
 * and later sends on that thread pass it.
 */

export const DASHBOARD_ASSISTANT_WS_METHODS = {
  send: "assistant.send",
  cancel: "assistant.cancel",
} as const;

export const DASHBOARD_ASSISTANT_WS_CHANNELS = {
  event: "dashboard.assistant.event",
} as const;

const OpaqueId = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128));

export const DashboardAssistantThreadId = OpaqueId.pipe(Schema.brand("DashboardAssistantThreadId"));
export type DashboardAssistantThreadId = typeof DashboardAssistantThreadId.Type;

export const DashboardAssistantTurnId = OpaqueId.pipe(Schema.brand("DashboardAssistantTurnId"));
export type DashboardAssistantTurnId = typeof DashboardAssistantTurnId.Type;

/** A dashboard's slug or id. */
export const DashboardRef = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200));
export type DashboardRef = typeof DashboardRef.Type;

// Client to server ---------------------------------------------------------

export const DashboardAssistantSendInput = Schema.Struct({
  dashboardRef: Schema.NullOr(DashboardRef),
  threadId: DashboardAssistantThreadId,
  text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(8_000)),
  /** The widget the person has selected, if any. */
  focus: Schema.optionalKey(Schema.Struct({ widgetId: Identifier })),
});
export type DashboardAssistantSendInput = typeof DashboardAssistantSendInput.Type;

/** The response `result` of `assistant.send`; events for the turn carry `turnId`. */
export const DashboardAssistantSendResult = Schema.Struct({
  threadId: DashboardAssistantThreadId,
  turnId: DashboardAssistantTurnId,
});
export type DashboardAssistantSendResult = typeof DashboardAssistantSendResult.Type;

/** Cancels the thread's running turn (or `turnId`, when given). */
export const DashboardAssistantCancelInput = Schema.Struct({
  threadId: DashboardAssistantThreadId,
  turnId: Schema.optionalKey(DashboardAssistantTurnId),
});
export type DashboardAssistantCancelInput = typeof DashboardAssistantCancelInput.Type;

// Server to client ---------------------------------------------------------

const EventBase = {
  threadId: DashboardAssistantThreadId,
  turnId: DashboardAssistantTurnId,
};

/** Streamed assistant text; append `delta` to the message `messageId`. */
export const DashboardAssistantMessageDelta = Schema.Struct({
  type: Schema.Literal("message.delta"),
  ...EventBase,
  messageId: OpaqueId,
  delta: Schema.String,
});

/** The message's final text, replacing the streamed deltas. */
export const DashboardAssistantMessageDone = Schema.Struct({
  type: Schema.Literal("message.done"),
  ...EventBase,
  messageId: OpaqueId,
  text: Schema.String,
});

/** A plain-language step, e.g. "Looking for a calls metric". Same `activityId` updates it. */
export const DashboardAssistantActivity = Schema.Struct({
  type: Schema.Literal("activity"),
  ...EventBase,
  activityId: OpaqueId,
  text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  state: Schema.Literals(["running", "done", "failed"]),
});

/** Edits saved as `version` of `dashboardRef`, with their plain-language summary. */
export const DashboardAssistantEditsApplied = Schema.Struct({
  type: Schema.Literal("edits.applied"),
  ...EventBase,
  dashboardRef: DashboardRef,
  version: PositiveCount,
  ops: Schema.Array(DashboardEditOperation).check(Schema.isMinLength(1)),
  summary: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(1_000)),
});

/**
 * A new dashboard exists (empty, a draft) and the turn is about to fill it in.
 * Pages open it at once, so a build is watched where the dashboard lives.
 */
export const DashboardAssistantDashboardCreated = Schema.Struct({
  type: Schema.Literal("dashboard.created"),
  ...EventBase,
  dashboardRef: DashboardRef,
  /** The dashboard's id, which per-dashboard thread state is keyed by. */
  dashboardId: DashboardRef,
});

/** A widget's data before it is saved, shaped like a resolve stream line. */
export const DashboardAssistantPreview = Schema.Struct({
  type: Schema.Literal("preview"),
  ...EventBase,
  ...DashboardWidgetResolutionFields,
}).check(dashboardWidgetResolutionFilter);

/** A quick answer that is not a dashboard (yet). */
export const DashboardAssistantAnswer = Schema.Struct({
  type: Schema.Literal("answer"),
  ...EventBase,
  values: Schema.Array(MetricValue),
  provenance: Schema.Array(Provenance).check(Schema.isMinLength(1)),
  freshness: DashboardFreshness,
});

/** `plain` is shown to the person; `details` is for the "details" disclosure and logs. */
export const DashboardAssistantError = Schema.Struct({
  type: Schema.Literal("error"),
  ...EventBase,
  plain: ShortText.check(Schema.isMinLength(1)),
  details: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(4_000))),
});

/** The turn is over; no more events follow for `turnId`. */
export const DashboardAssistantDone = Schema.Struct({
  type: Schema.Literal("done"),
  ...EventBase,
  reason: Schema.Literals(["completed", "cancelled", "failed"]),
});

export const DashboardAssistantEvent = Schema.Union([
  DashboardAssistantMessageDelta,
  DashboardAssistantMessageDone,
  DashboardAssistantActivity,
  DashboardAssistantDashboardCreated,
  DashboardAssistantEditsApplied,
  DashboardAssistantPreview,
  DashboardAssistantAnswer,
  DashboardAssistantError,
  DashboardAssistantDone,
]);
export type DashboardAssistantEvent = typeof DashboardAssistantEvent.Type;
export type DashboardAssistantEventType = DashboardAssistantEvent["type"];

export const DASHBOARD_ASSISTANT_EVENT_TYPES = [
  "message.delta",
  "message.done",
  "activity",
  "dashboard.created",
  "edits.applied",
  "preview",
  "answer",
  "error",
  "done",
] as const satisfies ReadonlyArray<DashboardAssistantEventType>;
